/*
 * =====================================================================================
 * DISCLAIMER:
 * =====================================================================================
 * This localization file is a community contribution and is not officially maintained
 * by Clerk. It has been provided by the community and may not be fully aligned
 * with the current or future states of the main application. Clerk does not guarantee
 * the accuracy, completeness, or timeliness of the translations in this file.
 * Use of this file is at your own risk and discretion.
 * =====================================================================================
 */

import type { LocalizationResource } from '@clerk/types';

export const daDK: LocalizationResource = {
  locale: 'da-DK',
  __experimental_userVerification: {
    alternativeMethods: {
      actionLink: undefined,
      actionText: undefined,
      blockButton__backupCode: undefined,
      blockButton__emailCode: undefined,
      blockButton__password: undefined,
      blockButton__phoneCode: undefined,
      blockButton__totp: undefined,
      getHelp: {
        blockButton__emailSupport: undefined,
        content: undefined,
        title: undefined,
      },
      subtitle: undefined,
      title: undefined,
    },
    backupCodeMfa: {
      subtitle: undefined,
      title: undefined,
    },
    emailCode: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    noAvailableMethods: {
      message: undefined,
      subtitle: undefined,
      title: undefined,
    },
    password: {
      actionLink: undefined,
      subtitle: undefined,
      title: undefined,
    },
    phoneCode: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    phoneCodeMfa: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    totpMfa: {
      formTitle: undefined,
      subtitle: undefined,
      title: undefined,
    },
  },
  backButton: 'Tilbage',
  badge__default: 'Standard',
  badge__otherImpersonatorDevice: 'Anden enhed som efterligner',
  badge__primary: 'Primær',
  badge__requiresAction: 'Kræver handling',
  badge__thisDevice: 'Denne enhed',
  badge__unverified: 'Ikke verificeret',
  badge__userDevice: 'Brugerenhed',
  badge__you: 'Dig',
  createOrganization: {
    formButtonSubmit: 'Opret organisation',
    invitePage: {
      formButtonReset: 'Spring over',
    },
    title: 'Opret organisation',
  },
  dates: {
    lastDay: "I går: {{ date | timeString('en-US') }}",
    next6Days: "{{ date | weekday('en-US','long') }} kl. {{ date | timeString('en-US') }}",
    nextDay: "I morgen: {{ date | timeString('en-US') }}",
    numeric: "{{ date | numeric('en-US') }}",
    previous6Days: "Sidste {{ date | weekday('en-US','long') }} kl. {{ date | timeString('en-US') }}",
    sameDay: "I dag: {{ date | timeString('en-US') }}",
  },
  dividerText: 'eller',
  footerActionLink__useAnotherMethod: 'Brug en anden metode',
  footerPageLink__help: 'Hjælp',
  footerPageLink__privacy: 'Privatliv',
  footerPageLink__terms: 'Vilkår',
  formButtonPrimary: 'Fortsæt',
  formButtonPrimary__verify: 'Verificer',
  formFieldAction__forgotPassword: 'Glemt adgangskode?',
  formFieldError__matchingPasswords: 'Adgangskoderne matcher.',
  formFieldError__notMatchingPasswords: 'Adgangskoderne matcher ikke.',
  formFieldError__verificationLinkExpired: 'Bekræftelseslinket er udløbet. Venligst anmod om et nyt link.',
  formFieldHintText__optional: 'Valgfri',
  formFieldHintText__slug: 'En slug er en menneskelig læsbar ID, der skal være unik. Det bruges ofte i URL’er.',
  formFieldInputPlaceholder__backupCode: 'Indtast sikkerhedskode',
  formFieldInputPlaceholder__confirmDeletionUserAccount: 'Slet konto',
  formFieldInputPlaceholder__emailAddress: 'Indtast e-mailadresse',
  formFieldInputPlaceholder__emailAddress_username: 'Indtast e-mailadresse eller brugernavn',
  formFieldInputPlaceholder__emailAddresses:
    'Indtast eller indsæt en eller flere e-mailadresser, adskilt af mellemrum eller kommaer',
  formFieldInputPlaceholder__firstName: 'Indtast fornavn',
  formFieldInputPlaceholder__lastName: 'Indtast efternavn',
  formFieldInputPlaceholder__organizationDomain: 'Indtast domæne',
  formFieldInputPlaceholder__organizationDomainEmailAddress: 'Indtast e-mailadresse til verifikation',
  formFieldInputPlaceholder__organizationName: 'Indtast organisationens navn',
  formFieldInputPlaceholder__organizationSlug: 'Indtast slug URL',
  formFieldInputPlaceholder__password: 'Indtast adgangskode',
  formFieldInputPlaceholder__phoneNumber: 'Indtast telefonnummer',
  formFieldInputPlaceholder__username: 'Indtast brugernavn',
  formFieldLabel__automaticInvitations: 'Aktiver automatiske invitationer for dette domæne',
  formFieldLabel__backupCode: 'Sikkerhedskode',
  formFieldLabel__confirmDeletion: 'Bekræftelse',
  formFieldLabel__confirmPassword: 'Bekræft adgangskode',
  formFieldLabel__currentPassword: 'Nuværende adgangskode',
  formFieldLabel__emailAddress: 'E-mailadresse',
  formFieldLabel__emailAddress_username: 'E-mailadresse eller brugernavn',
  formFieldLabel__emailAddresses: 'E-mailadresser',
  formFieldLabel__firstName: 'Fornavn',
  formFieldLabel__lastName: 'Efternavn',
  formFieldLabel__newPassword: 'Ny adgangskode',
  formFieldLabel__organizationDomain: 'Domæne',
  formFieldLabel__organizationDomainDeletePending: 'Slet ventende invitationer og forslag',
  formFieldLabel__organizationDomainEmailAddress: 'Verifikation e-mailadresse',
  formFieldLabel__organizationDomainEmailAddressDescription:
    'Indtast en e-mailadresse under dette domæne for at modtage en kode og verificere dette domæne.',
  formFieldLabel__organizationName: 'Organisationens navn',
  formFieldLabel__organizationSlug: 'Slug URL',
  formFieldLabel__passkeyName: 'Navn på adgangsnøgle',
  formFieldLabel__password: 'Adgangskode',
  formFieldLabel__phoneNumber: 'Telefonnummer',
  formFieldLabel__role: 'Rolle',
  formFieldLabel__signOutOfOtherSessions: 'Log ud af alle andre enheder',
  formFieldLabel__username: 'Brugernavn',
  impersonationFab: {
    action__signOut: 'Log ud',
    title: 'Logget ind som {{identifier}}',
  },
  maintenanceMode: 'Vedligeholdelsestilstand',
  membershipRole__admin: 'Administrator',
  membershipRole__basicMember: 'Medlem',
  membershipRole__guestMember: 'Gæst',
  organizationList: {
    action__createOrganization: 'Opret organisation',
    action__invitationAccept: 'Deltag',
    action__suggestionsAccept: 'Anmod om at deltage',
    createOrganization: 'Opret Organisation',
    invitationAcceptedLabel: 'Deltaget',
    subtitle: 'for at fortsætte til {{applicationName}}',
    suggestionsAcceptedLabel: 'Afventer godkendelse',
    title: 'Vælg en konto',
    titleWithoutPersonal: 'Vælg en organisation',
  },
  organizationProfile: {
    badge__automaticInvitation: 'Automatiske invitationer',
    badge__automaticSuggestion: 'Automatiske forslag',
    badge__manualInvitation: 'Ingen automatisk tilmelding',
    badge__unverified: 'Ikke verificeret',
    createDomainPage: {
      subtitle:
        'Tilføj domænet for at verificere. Brugere med e-mailadresser under dette domæne kan automatisk deltage i organisationen eller anmode om at deltage.',
      title: 'Tilføj domæne',
    },
    invitePage: {
      detailsTitle__inviteFailed: 'Invitationerne kunne ikke sendes. Ret følgende, og prøv igen:',
      formButtonPrimary__continue: 'Send invitationer',
      selectDropdown__role: 'Vælg rolle',
      subtitle: 'Inviter nye medlemmer til denne organisation',
      successMessage: 'Invitationer blev sendt',
      title: 'Inviter medlemmer',
    },
    membersPage: {
      action__invite: 'Inviter',
      activeMembersTab: {
        menuAction__remove: 'Fjern medlem',
        tableHeader__actions: undefined,
        tableHeader__joined: 'Deltaget',
        tableHeader__role: 'Rolle',
        tableHeader__user: 'Bruger',
      },
      detailsTitle__emptyRow: 'Ingen medlemmer at vise',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            'Inviter brugere ved at forbinde et e-maildomæne med din organisation. Alle, der tilmelder sig med en matchende e-maildomæne, vil kunne deltage i organisationen når som helst.',
          headerTitle: 'Automatiske invitationer',
          primaryButton: 'Administrer verificerede domæner',
        },
        table__emptyRow: 'Ingen invitationer at vise',
      },
      invitedMembersTab: {
        menuAction__revoke: 'Tilbagekald invitation',
        tableHeader__invited: 'Inviteret',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle:
            'Brugere, der tilmelder sig med en matchende e-maildomæne, vil kunne se et forslag om at anmode om at deltage i din organisation.',
          headerTitle: 'Automatiske forslag',
          primaryButton: 'Administrer verificerede domæner',
        },
        menuAction__approve: 'Godkend',
        menuAction__reject: 'Afvis',
        tableHeader__requested: 'Anmodet adgang',
        table__emptyRow: 'Ingen anmodninger at vise',
      },
      start: {
        headerTitle__invitations: 'Invitationer',
        headerTitle__members: 'Medlemmer',
        headerTitle__requests: 'Anmodninger',
      },
    },
    navbar: {
      description: 'Administrer din organisation.',
      general: 'Generelt',
      members: 'Medlemmer',
      title: 'Organisation',
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: 'Skriv "{{organizationName}}" nedenfor for at fortsætte.',
          messageLine1: 'Er du sikker på, at du vil slette denne organisation?',
          messageLine2: 'Denne handling er permanent og kan ikke fortrydes.',
          successMessage: 'Du har slettet organisationen.',
          title: 'Slet organisation',
        },
        leaveOrganization: {
          actionDescription: 'Skriv "{{organizationName}}" nedenfor for at fortsætte.',
          messageLine1:
            'Er du sikker på, at du vil forlade denne organisation? Du mister adgang til denne organisation og dens applikationer.',
          messageLine2: 'Denne handling er permanent og kan ikke fortrydes.',
          successMessage: 'Du har forladt organisationen.',
          title: 'Forlad organisationen',
        },
        title: 'Fare',
      },
      domainSection: {
        menuAction__manage: 'Administrer',
        menuAction__remove: 'Slet',
        menuAction__verify: 'Verificer',
        primaryButton: 'Tilføj domæne',
        subtitle:
          'Tillad brugere at deltage i organisationen automatisk eller anmode om at deltage baseret på et verificeret e-maildomæne.',
        title: 'Verificerede domæner',
      },
      successMessage: 'Organisationen er blevet opdateret.',
      title: 'Organisationsprofil',
    },
    removeDomainPage: {
      messageLine1: 'E-maildomænet {{domain}} vil blive fjernet.',
      messageLine2: 'Brugere vil ikke længere kunne deltage i organisationen automatisk efter dette.',
      successMessage: '{{domain}} er blevet fjernet.',
      title: 'Fjern domæne',
    },
    start: {
      headerTitle__general: 'Generelt',
      headerTitle__members: 'Medlemmer',
      profileSection: {
        primaryButton: 'Opdater profil',
        title: 'Organisationsprofil',
        uploadAction__title: 'Logo',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: 'Fjernelse af dette domæne vil påvirke inviterede brugere.',
        removeDomainActionLabel__remove: 'Fjern domæne',
        removeDomainSubtitle: 'Fjern dette domæne fra dine verificerede domæner',
        removeDomainTitle: 'Fjern domæne',
      },
      enrollmentTab: {
        automaticInvitationOption__description:
          'Brugere bliver automatisk inviteret til at deltage i organisationen, når de tilmelder sig og kan deltage når som helst.',
        automaticInvitationOption__label: 'Automatiske invitationer',
        automaticSuggestionOption__description:
          'Brugere modtager et forslag om at anmode om at deltage, men skal godkendes af en administrator, før de kan deltage i organisationen.',
        automaticSuggestionOption__label: 'Automatiske forslag',
        calloutInfoLabel: 'Ændring af tilmeldingsindstilling vil kun påvirke nye brugere.',
        calloutInvitationCountLabel: 'Ventende invitationer sendt til brugere: {{count}}',
        calloutSuggestionCountLabel: 'Ventende forslag sendt til brugere: {{count}}',
        manualInvitationOption__description: 'Brugere kan kun inviteres manuelt til organisationen.',
        manualInvitationOption__label: 'Ingen automatisk tilmelding',
        subtitle: 'Vælg hvordan brugere fra dette domæne kan deltage i organisationen.',
      },
      start: {
        headerTitle__danger: 'Fare',
        headerTitle__enrollment: 'Tilmeldingsindstillinger',
      },
      subtitle: 'Domænet {{domain}} er nu verificeret. Fortsæt ved at vælge tilmeldingsmetode.',
      title: 'Opdater {{domain}}',
    },
    verifyDomainPage: {
      formSubtitle: 'Indtast den bekræftelseskode, der blev sendt til din e-mailadresse',
      formTitle: 'Bekræftelseskode',
      resendButton: 'Modtog du ikke en kode? Send igen',
      subtitle: 'Domænet {{domainName}} skal verificeres via e-mail.',
      subtitleVerificationCodeScreen:
        'En bekræftelseskode blev sendt til {{emailAddress}}. Indtast koden for at fortsætte.',
      title: 'Verificer domæne',
    },
  },
  organizationSwitcher: {
    action__createOrganization: 'Opret organisation',
    action__invitationAccept: 'Deltag',
    action__manageOrganization: 'Administrer organisation',
    action__suggestionsAccept: 'Anmod om at deltage',
    notSelected: 'Ingen organisation valgt',
    personalWorkspace: 'Personligt arbejdsområde',
    suggestionsAcceptedLabel: 'Afventer godkendelse',
  },
  paginationButton__next: 'Næste',
  paginationButton__previous: 'Forrige',
  paginationRowText__displaying: 'Viser',
  paginationRowText__of: 'af',
  signIn: {
    accountSwitcher: {
      action__addAccount: 'Tilføj konto',
      action__signOutAll: 'Log ud af alle konti',
      subtitle: 'Vælg den konto, du ønsker at fortsætte med.',
      title: 'Vælg en konto',
    },
    alternativeMethods: {
      actionLink: 'Få hjælp',
      actionText: 'Har du ingen af disse?',
      blockButton__backupCode: 'Brug en backup-kode',
      blockButton__emailCode: 'Send kode til {{identifier}}',
      blockButton__emailLink: 'Send link til {{identifier}}',
      blockButton__passkey: 'Brug en adgangsnøgle',
      blockButton__password: 'Log ind med adgangskode',
      blockButton__phoneCode: 'Send kode til {{identifier}}',
      blockButton__totp: 'Brug din godkendelsesapp',
      getHelp: {
        blockButton__emailSupport: 'E-mail support',
        content:
          'Hvis du har problemer med at logge ind på din konto, skal du sende en e-mail til os, og vi vil samarbejde med dig om at genoprette adgang så hurtigt som muligt.',
        title: 'Få hjælp',
      },
      subtitle: 'Oplever du problemer? Du kan bruge en af disse metoder til at logge ind.',
      title: 'Brug en anden metode',
    },
    backupCodeMfa: {
      subtitle: 'Fortsæt til {{applicationName}}',
      title: 'Indtast en backup-kode',
    },
    emailCode: {
      formTitle: 'Bekræftelseskode',
      resendButton: 'Send kode igen',
      subtitle: 'Fortsæt til {{applicationName}}',
      title: 'Tjek din email',
    },
    emailLink: {
      clientMismatch: {
        subtitle: 'Klient uoverensstemmelse. Prøv igen.',
        title: 'Klient fejl',
      },
      expired: {
        subtitle: 'Vend tilbage til den oprindelige fane for at fortsætte.',
        title: 'Dette bekræftelseslink er udløbet',
      },
      failed: {
        subtitle: 'Vend tilbage til den oprindelige fane for at fortsætte.',
        title: 'Dette bekræftelseslink er ugyldigt',
      },
      formSubtitle: 'Brug bekræftelseslinket sendt til din e-mail',
      formTitle: 'Bekræftelseslink',
      loading: {
        subtitle: 'Du vil snart blive viderestillet',
        title: 'Logger ind...',
      },
      resendButton: 'Send link igen',
      subtitle: 'Fortsæt til {{applicationName}}',
      title: 'Tjek din email',
      unusedTab: {
        title: 'Du kan lukke denne fane',
      },
      verified: {
        subtitle: 'Du vil snart blive viderestillet',
        title: 'Vellykket log ind',
      },
      verifiedSwitchTab: {
        subtitle: 'Vend tilbage til den oprindelige fane for at fortsætte',
        subtitleNewTab: 'Vend tilbage til den nyligt åbnede fane for at fortsætte',
        titleNewTab: 'Logget ind på anden fane',
      },
    },
    forgotPassword: {
      formTitle: 'Nulstil adgangskode',
      resendButton: 'Modtog du ikke en kode? Send igen',
      subtitle: 'for at nulstille din adgangskode',
      subtitle_email: 'Indtast først koden sendt til din e-mailadresse',
      subtitle_phone: 'Indtast først koden sendt til din telefon',
      title: 'Nulstil adgangskode',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: 'Nulstil din adgangskode',
      label__alternativeMethods: 'Eller, log ind med en anden metode',
      title: 'Glemt Adgangskode?',
    },
    noAvailableMethods: {
      message: 'Kan ikke fortsætte med login. Der er ingen tilgængelige godkendelsesfaktorer.',
      subtitle: 'En fejl opstod',
      title: 'Kan ikke logge ind',
    },
    passkey: {
      subtitle: 'Brug adgangsnøgle til at logge ind.',
      title: 'Adgangsnøgle',
    },
    password: {
      actionLink: 'Brug en anden metode',
      subtitle: 'Fortsæt til {{applicationName}}',
      title: 'Indtast din adgangskode',
    },
    passwordPwned: {
      title: 'Sikkerhedsadvarsel',
    },
    phoneCode: {
      formTitle: 'Bekræftelseskode',
      resendButton: 'Send kode igen',
      subtitle: 'gå videre til {{applicationName}}',
      title: 'Tjek din telefon',
    },
    phoneCodeMfa: {
      formTitle: 'Bekræftelseskode',
      resendButton: 'Send kode igen',
      subtitle: undefined,
      title: 'Tjek din telefon',
    },
    resetPassword: {
      formButtonPrimary: 'Nulstil Adgangskode',
      requiredMessage: 'Af sikkerhedsmæssige årsager er det nødvendigt at nulstille din adgangskode.',
      successMessage: 'Din adgangskode blev ændret med succes. Logger dig ind, vent venligst et øjeblik.',
      title: 'Indstil ny adgangskode',
    },
    resetPasswordMfa: {
      detailsLabel: 'Vi skal bekræfte din identitet, før du nulstiller din adgangskode.',
    },
    start: {
      actionLink: 'Tilmeld dig',
      actionLink__join_waitlist: undefined,
      actionLink__use_email: 'Brug email',
      actionLink__use_email_username: 'Brug email eller brugernavn',
      actionLink__use_passkey: 'Brug adgangsnøgle',
      actionLink__use_phone: 'Brug telefon',
      actionLink__use_username: 'Brug brugernavn',
      actionText: 'Ingen konto?',
      actionText__join_waitlist: undefined,
      subtitle: 'Forsæt til {{applicationName}}',
      title: 'Log ind',
    },
    totpMfa: {
      formTitle: 'Bekræftelseskode',
      subtitle: undefined,
      title: 'Totrinsbekræftelse',
    },
  },
  signInEnterPasswordTitle: 'Indtast din adgangskode',
  signUp: {
    continue: {
      actionLink: 'Log ind',
      actionText: 'Har du en konto?',
      subtitle: 'Forsæt til {{applicationName}}',
      title: 'Udfyld manglende felter',
    },
    emailCode: {
      formSubtitle: 'Indtast bekræftelseskoden sendt til din e-mailadresse',
      formTitle: 'Bekræftelseskode',
      resendButton: 'Send kode igen',
      subtitle: 'Fortsæt til {{applicationName}}',
      title: 'Bekræft din email',
    },
    emailLink: {
      clientMismatch: {
        subtitle: 'Klient uoverensstemmelse. Prøv igen.',
        title: 'Klient fejl',
      },
      formSubtitle: 'Brug bekræftelseslinket sendt til din e-mailadresse',
      formTitle: 'Bekræftelseslink',
      loading: {
        title: 'Tilmelding...',
      },
      resendButton: 'Send link igen',
      subtitle: 'Forsæt til {{applicationName}}',
      title: 'Bekræft din email',
      verified: {
        title: 'Vellykket tilmelding',
      },
      verifiedSwitchTab: {
        subtitle: 'Vend tilbage til den nyligt åbnede fane for at fortsætte',
        subtitleNewTab: 'Vend tilbage til forrige fane for at fortsætte',
        title: 'E-mail er bekræftet',
      },
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: undefined,
        label__onlyTermsOfService: undefined,
        label__termsOfServiceAndPrivacyPolicy: undefined,
      },
      continue: {
        subtitle: undefined,
        title: undefined,
      },
    },
    phoneCode: {
      formSubtitle: 'Indtast bekræftelseskoden sendt til dit telefonnummer',
      formTitle: 'Bekræftelseskode',
      resendButton: 'Send kode igen',
      subtitle: 'Fortsæt til {{applicationName}}',
      title: 'Bekræft din telefon',
    },
    restrictedAccess: {
      actionLink: undefined,
      actionText: undefined,
      blockButton__emailSupport: undefined,
      blockButton__joinWaitlist: undefined,
      subtitle: undefined,
      subtitleWaitlist: undefined,
      title: undefined,
    },
    start: {
      actionLink: 'Log ind',
      actionLink__use_email: 'Brug email',
      actionLink__use_phone: 'Brug telefon',
      actionText: 'Har du en konto?',
      subtitle: 'Forsæt til {{applicationName}}',
      title: 'Opret din konto',
    },
  },
  socialButtonsBlockButton: 'Forsæt med {{provider|titleize}}',
  socialButtonsBlockButtonManyInView: 'Vælg en metode til at fortsætte',
  unstable__errors: {
    already_a_member_in_organization: undefined,
    captcha_invalid:
      'Tilmelding mislykkedes på grund af fejlede sikkerhedsvalideringer. Opdater siden for at prøve igen, eller kontakt support for yderligere assistance.',
    captcha_unavailable:
      'Tilmelding mislykkedes på grund af fejlet botvalidering. Opdater siden for at prøve igen, eller kontakt support for yderligere assistance.',
    form_code_incorrect: 'Koden er forkert.',
    form_identifier_exists: 'Identifikatoren findes allerede.',
    form_identifier_exists__email_address: 'E-mailadressen er allerede i brug.',
    form_identifier_exists__phone_number: 'Telefonnummeret er allerede i brug.',
    form_identifier_exists__username: 'Brugernavnet er allerede i brug.',
    form_identifier_not_found: 'Identifikatoren blev ikke fundet.',
    form_param_format_invalid: 'Formatet er ugyldigt.',
    form_param_format_invalid__email_address: 'E-mailadressen skal være en gyldig e-mailadresse.',
    form_param_format_invalid__phone_number: 'Telefonnummeret skal være i et gyldigt internationalt format.',
    form_param_max_length_exceeded__first_name: 'Fornavnet må ikke overstige 256 tegn.',
    form_param_max_length_exceeded__last_name: 'Efternavnet må ikke overstige 256 tegn.',
    form_param_max_length_exceeded__name: 'Navnet må ikke overstige 256 tegn.',
    form_param_nil: 'Dette felt kan ikke være tomt.',
    form_param_value_invalid: undefined,
    form_password_incorrect: 'Adgangskoden er forkert.',
    form_password_length_too_short: 'Adgangskoden er for kort.',
    form_password_not_strong_enough: 'Adgangskoden er ikke stærk nok.',
    form_password_pwned: 'Adgangskoden er blevet kompromitteret.',
    form_password_pwned__sign_in: 'Din adgangskode er blevet kompromitteret, vælg en ny.',
    form_password_size_in_bytes_exceeded:
      'Din adgangskode har overskredet det maksimalt tilladte antal bytes, forkort den eller fjern nogle specialtegn.',
    form_password_validation_failed: 'Forkert adgangskode.',
    form_username_invalid_character: 'Brugernavnet indeholder ugyldige tegn.',
    form_username_invalid_length: 'Brugernavnet har en ugyldig længde.',
    identification_deletion_failed: 'Du kan ikke slette din sidste identifikation.',
    not_allowed_access: 'Adgang ikke tilladt.',
    organization_domain_blocked: undefined,
    organization_domain_common: undefined,
    organization_membership_quota_exceeded: undefined,
    organization_minimum_permissions_needed: undefined,
    passkey_already_exists: 'Adgangsnøgle findes allerede.',
    passkey_not_supported: 'Adgangsnøgler understøttes ikke på denne enhed.',
    passkey_pa_not_supported: 'Adgangsnøgler understøttes ikke på dette styresystem.',
    passkey_registration_cancelled: 'Registrering af adgangsnøgle blev annulleret.',
    passkey_retrieval_cancelled: 'Hentning af adgangsnøgle blev annulleret.',
    passwordComplexity: {
      maximumLength: 'mindre end {{length}} tegn',
      minimumLength: '{{length}} eller flere tegn',
      requireLowercase: 'et lille bogstav',
      requireNumbers: 'et tal',
      requireSpecialCharacter: 'et specialtegn',
      requireUppercase: 'et stort bogstav',
      sentencePrefix: 'Din adgangskode skal indeholde',
    },
    phone_number_exists: 'Dette telefonnummer er allerede taget. Prøv et andet.',
    zxcvbn: {
      couldBeStronger: 'Din adgangskode virker, men kunne være stærkere. Prøv at tilføje flere tegn.',
      goodPassword: 'Din adgangskode opfylder alle nødvendige krav.',
      notEnough: 'Din adgangskode er ikke stærk nok.',
      suggestions: {
        allUppercase: 'Brug store bogstaver på nogle, men ikke alle bogstaver.',
        anotherWord: 'Tilføj flere ord, der er mindre almindelige.',
        associatedYears: 'Undgå år, der er forbundet med dig.',
        capitalization: 'Brug store bogstaver på mere end det første bogstav.',
        dates: 'Undgå datoer og år, der er forbundet med dig.',
        l33t: "Undgå forudsigelige bogstavsubstitutioner som '@' for 'a'.",
        longerKeyboardPattern: 'Brug længere tastaturmønstre og skift retning flere gange.',
        noNeed: 'Du kan oprette stærke adgangskoder uden at bruge symboler, tal eller store bogstaver.',
        pwned: 'Hvis du bruger denne adgangskode andre steder, bør du ændre den.',
        recentYears: 'Undgå nylige årstal.',
        repeated: 'Undgå gentagne ord og tegn.',
        reverseWords: 'Undgå omvendte stavemåder af almindelige ord.',
        sequences: 'Undgå almindelige tegnsekvenser.',
        useWords: 'Brug flere ord, men undgå almindelige sætninger.',
      },
      warnings: {
        common: 'Dette er en almindeligt brugt adgangskode.',
        commonNames: 'Almindelige navne og efternavne er nemme at gætte.',
        dates: 'Datoer er nemme at gætte.',
        extendedRepeat: 'Gentagne tegnmønstre som "abcabcabc" er nemme at gætte.',
        keyPattern: 'Korte tastaturmønstre er nemme at gætte.',
        namesByThemselves: 'Enkeltstående navne eller efternavne er nemme at gætte.',
        pwned: 'Din adgangskode er blevet eksponeret af et databrud på internettet.',
        recentYears: 'Nylige årstal er nemme at gætte.',
        sequences: 'Almindelige tegnsekvenser som "abc" er nemme at gætte.',
        similarToCommon: 'Dette ligner en almindeligt brugt adgangskode.',
        simpleRepeat: 'Gentagne tegn som "aaa" er nemme at gætte.',
        straightRow: 'Lige rækker af taster på dit tastatur er nemme at gætte.',
        topHundred: 'Dette er en ofte brugt adgangskode.',
        topTen: 'Dette er en meget brugt adgangskode.',
        userInputs: 'Der bør ikke være nogen personlige eller sidespecifikke data.',
        wordByItself: 'Enkeltstående ord er nemme at gætte.',
      },
    },
  },
  userButton: {
    action__addAccount: 'Tilføj konto',
    action__manageAccount: 'Administrer konto',
    action__signOut: 'Log ud',
    action__signOutAll: 'Log ud af alle konti',
  },
  userProfile: {
    backupCodePage: {
      actionLabel__copied: 'Kopieret!',
      actionLabel__copy: 'Kopier alle',
      actionLabel__download: 'Download .txt',
      actionLabel__print: 'Print',
      infoText1: 'Backup-koder vil blive aktiveret for denne konto.',
      infoText2:
        'Hold backup-koderne hemmelige og gem dem sikkert. Du kan genskabe backup-koder, hvis du har mistanke om, at de er blevet kompromitteret.',
      subtitle__codelist: 'Opbevar dem sikkert og hold dem hemmelige.',
      successMessage:
        'Backup-koder er nu aktiveret. Du kan bruge en af disse til at logge ind på din konto, hvis du mister adgangen til din totrinsbekræftelse. Hver kode kan kun bruges én gang.',
      successSubtitle:
        'Du kan bruge en af disse til at logge ind på din konto, hvis du mister adgangen til din totrinsbekræftelse.',
      title: 'Tilføj bekræftelse af backup-kode',
      title__codelist: 'Backup-koder',
    },
    connectedAccountPage: {
      formHint: 'Vælg en udbyder for at forbinde din konto.',
      formHint__noAccounts: 'Der er ingen tilgængelige eksterne kontoudbydere.',
      removeResource: {
        messageLine1: '{{identifier}} vil blive fjernet fra denne konto.',
        messageLine2:
          'Du vil ikke længere være i stand til at bruge denne tilsluttede konto, og eventuelle afhængige funktioner vil ikke længere virke.',
        successMessage: '{{connectedAccount}} er blevet fjernet fra din konto.',
        title: 'Fjern tilsluttet konto',
      },
      socialButtonsBlockButton: 'Forbind {{provider|titleize}} konto',
      successMessage: 'Udbyderen er blevet tilføjet til din konto',
      title: 'Tilføj tilsluttet konto',
    },
    deletePage: {
      actionDescription: 'Skriv "Slet konto" nedenfor for at fortsætte.',
      confirm: 'Slet konto',
      messageLine1: 'Er du sikker på, at du vil slette din konto?',
      messageLine2: 'Denne handling er permanent og kan ikke fortrydes.',
      title: 'Slet konto',
    },
    emailAddressPage: {
      emailCode: {
        formHint: 'En e-mail indeholdende en bekræftelseskode vil blive sendt til denne e-mailadresse.',
        formSubtitle: 'Indtast bekræftelseskoden sendt til {{identifier}}',
        formTitle: 'Bekræftelseskode',
        resendButton: 'Send kode igen',
        successMessage: 'E-mailen {{identifier}} er blevet tilføjet til din konto.',
      },
      emailLink: {
        formHint: 'En e-mail indeholdende et bekræftelseslink vil blive sendt til denne e-mailadresse.',
        formSubtitle: 'Klik på bekræftelseslinket i e-mailen sendt til {{identifier}}',
        formTitle: 'Bekræftelseslink',
        resendButton: 'Send link igen',
        successMessage: 'E-mailen {{identifier}} er blevet tilføjet til din konto.',
      },
      removeResource: {
        messageLine1: '{{identifier}} vil blive fjernet fra denne konto.',
        messageLine2: 'Du vil ikke længere kunne logge ind med denne e-mailadresse.',
        successMessage: '{{emailAddress}} er blevet fjernet fra din konto.',
        title: 'Fjern e-mailadresse',
      },
      title: 'Tilføj e-mailadresse',
      verifyTitle: 'Verificer e-mailadresse',
    },
    formButtonPrimary__add: 'Tilføj',
    formButtonPrimary__continue: 'Fortsæt',
    formButtonPrimary__finish: 'Afslut',
    formButtonPrimary__remove: 'Fjern',
    formButtonPrimary__save: 'Gem',
    formButtonReset: 'Annuller',
    mfaPage: {
      formHint: 'Vælg en metode, der skal tilføjes.',
      title: 'Tilføj totrinsbekræftelse',
    },
    mfaPhoneCodePage: {
      backButton: 'Brug eksisterende nummer',
      primaryButton__addPhoneNumber: 'Tilføj et telefonnummer',
      removeResource: {
        messageLine1: '{{identifier}} vil ikke længere modtage bekræftelseskoder, når du logger ind.',
        messageLine2: 'Din konto er muligvis ikke så sikker. Er du sikker på, at du vil fortsætte?',
        successMessage: 'SMS-bekræftelse er blevet fjernet for {{mfaPhoneCode}}',
        title: 'Fjern SMS-bekræftelse',
      },
      subtitle__availablePhoneNumbers:
        'Vælg et telefonnummer for at registrere SMS-bekræftelse til totrinsbekræftelse.',
      subtitle__unavailablePhoneNumbers:
        'Der er ingen tilgængelige telefonnumre til at registrere til SMS-bekræftelse til totrinsbekræftelse.',
      successMessage1:
        'Når du logger ind, skal du indtaste en bekræftelseskode sendt til dette telefonnummer som et ekstra trin.',
      successMessage2:
        'Gem disse backup-koder og opbevar dem et sikkert sted. Hvis du mister adgangen til din godkendelsesenhed, kan du bruge backup-koder til at logge ind.',
      successTitle: 'SMS-bekræftelse aktiveret',
      title: 'Tilføj SMS-bekræftelse',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: 'Scan QR-koden i stedet',
        buttonUnableToScan__nonPrimary: 'Kan du ikke scanne QR-koden?',
        infoText__ableToScan:
          'Konfigurer en ny login-metode i din autentificeringsapp, og scan følgende QR-kode for at linke den til din konto.',
        infoText__unableToScan: 'Konfigurer en ny login-metode i din autentificeringsapp, og indtast nøglen nedenfor.',
        inputLabel__unableToScan1:
          'Sørg for, at tidsbaserede eller engangsadgangskoder er aktiveret, og afslut derefter tilknytningen af din konto.',
        inputLabel__unableToScan2:
          "Alternativt, hvis din autentificeringsapp understøtter TOTP URI'er, kan du også kopiere hele URI'en.",
      },
      removeResource: {
        messageLine1: 'Bekræftelseskoder fra denne autentificeringsapp kræves ikke længere, når du logger ind.',
        messageLine2: 'Din konto er muligvis ikke så sikker. Er du sikker på, at du vil fortsætte?',
        successMessage: 'Totrinsbekræftelse via autentificeringsapp er blevet fjernet.',
        title: 'Fjern totrinsbekræftelse',
      },
      successMessage:
        'Når du logger ind, skal du indtaste en bekræftelseskode fra denne autentificeringsapp som et ekstra trin.',
      title: 'Tilføj autentificeringsapp',
      verifySubtitle: 'Indtast bekræftelseskode, der er genereret af din autentificeringsapp',
      verifyTitle: 'Bekræftelseskode',
    },
    mobileButton__menu: 'Menu',
    navbar: {
      account: 'Profil',
      description: 'Administrer dine kontooplysninger.',
      security: 'Sikkerhed',
      title: 'Konto',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: 'Adgangsnøglen fjernes fra din konto.',
        title: 'Fjern adgangsnøgle',
      },
      subtitle__rename: 'Omdøb adgangsnøgle',
      title__rename: 'Omdøb adgangsnøgle',
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions:
        'Det anbefales at logge ud af alle andre enheder, der kan have brugt din gamle adgangskode.',
      readonly:
        'Din adgangskode kan i øjeblikket ikke redigeres, fordi du kun kan logge ind via virksomhedens forbindelse.',
      successMessage__set: 'Din adgangskode er blevet indstillet.',
      successMessage__signOutOfOtherSessions: 'Alle andre enheder er blevet logget ud.',
      successMessage__update: 'Din adgangskode er blevet opdateret.',
      title__set: 'Indstil adgangskode',
      title__update: 'Skift adgangskode',
    },
    phoneNumberPage: {
      infoText: 'En SMS, der indeholder et bekræftelseslink, sendes til dette telefonnummer.',
      removeResource: {
        messageLine1: '{{identifier}} vil blive fjernet fra denne konto.',
        messageLine2: 'Du vil ikke længere kunne logge ind med dette telefonnummer.',
        successMessage: '{{phoneNumber}} er blevet fjernet fra din konto.',
        title: 'Fjern telefonnummer',
      },
      successMessage: '{{identifier}} er blevet tilføjet til din konto.',
      title: 'Tilføj telefonnummer',
      verifySubtitle: 'Indtast bekræftelseskoden sendt til {{identifier}}',
      verifyTitle: 'Verificer telefonnummer',
    },
    profilePage: {
      fileDropAreaHint: 'Upload et JPG, PNG, GIF, eller WEBP-billede mindre end 10 MB',
      imageFormDestructiveActionSubtitle: 'Fjern billede',
      imageFormSubtitle: 'Upload billede',
      imageFormTitle: 'Profilbillede',
      readonly: 'Dine profiloplysninger er leveret af virksomhedens forbindelse og kan ikke redigeres.',
      successMessage: 'Din profil er blevet opdateret.',
      title: 'Profil',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: 'Log ud af enhed',
        title: 'Aktive enheder',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: 'Prøv igen',
        actionLabel__reauthorize: 'Godkend nu',
        destructiveActionTitle: 'Fjern',
        primaryButton: 'Tilknyt konto',
        subtitle__disconnected: 'Ingen tilknyttede konti',
        subtitle__reauthorize:
          'De krævede tilladelser er blevet opdateret, og du kan opleve begrænset funktionalitet. Godkend venligst denne applikation igen for at undgå eventuelle problemer',
        title: 'Tilknyttede konti',
      },
      dangerSection: {
        deleteAccountButton: 'Slet Konto',
        title: 'Konto afslutning',
      },
      emailAddressesSection: {
        destructiveAction: 'Fjern e-mailadresse',
        detailsAction__nonPrimary: 'Sæt som primær',
        detailsAction__primary: 'Fuldfør bekræftelse',
        detailsAction__unverified: 'Fuldfør bekræftelse',
        primaryButton: 'Tilføj en e-mailadresse',
        title: 'E-mailadresser',
      },
      enterpriseAccountsSection: {
        title: 'Virksomhedskonti',
      },
      headerTitle__account: 'Konto',
      headerTitle__security: 'Sikkerhed',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: 'Generer koder',
          headerTitle: 'Backup-koder',
          subtitle__regenerate:
            'Få et nyt sæt sikre backup-koder. Tidligere backup-koder vil blive slettet og kan ikke bruges.',
          title__regenerate: 'Generer backup-koder',
        },
        phoneCode: {
          actionLabel__setDefault: 'Indstil som standard',
          destructiveActionLabel: 'Fjern telefonnummer',
        },
        primaryButton: 'Tilføj totrinsbekræftelse',
        title: 'Totrinsbekræftelse',
        totp: {
          destructiveActionTitle: 'Fjern',
          headerTitle: 'Autentificeringsapp',
        },
      },
      passkeysSection: {
        menuAction__destructive: 'Fjern adgangsnøgle',
        menuAction__rename: 'Omdøb adgangsnøgle',
        title: 'Adgangsnøgler',
      },
      passwordSection: {
        primaryButton__setPassword: 'Indtast adgangskode',
        primaryButton__updatePassword: 'Skift adgangskode',
        title: 'Adgangskode',
      },
      phoneNumbersSection: {
        destructiveAction: 'Fjern telefonnummer',
        detailsAction__nonPrimary: 'Sæt som primær',
        detailsAction__primary: 'Fuldfør bekræftelse',
        detailsAction__unverified: 'Fuldfør bekræftelse',
        primaryButton: 'Tilføj et telefonnummer',
        title: 'Telefonnumre',
      },
      profileSection: {
        primaryButton: 'Opdater profil',
        title: 'Profil',
      },
      usernameSection: {
        primaryButton__setUsername: 'Sæt brugernavn',
        primaryButton__updateUsername: 'Skift brugernavn',
        title: 'Brugernavn',
      },
      web3WalletsSection: {
        destructiveAction: 'Fjern tegnebog',
        primaryButton: 'Tilføj Web3 tegnebøger',
        title: 'Web3 tegnebøger',
      },
    },
    usernamePage: {
      successMessage: 'Dit brugernavn er blevet opdateret.',
      title__set: 'Indstil brugernavn',
      title__update: 'Opdater brugernavn',
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: '{{identifier}} vil blive fjernet fra denne konto.',
        messageLine2: 'Du vil ikke længere være i stand til at logge ind med denne web3-tegnebog.',
        successMessage: '{{web3Wallet}} er blevet fjernet fra din konto.',
        title: 'Fjern web3-tegnebog',
      },
      subtitle__availableWallets: 'Vælg en web3-tegnebog for at oprette forbindelse til din konto.',
      subtitle__unavailableWallets: 'Der er ingen tilgængelige web3-tegnebøger.',
      successMessage: 'Tegnebogen er blevet tilføjet til din konto.',
      title: 'Tilføj web3-tegnebog',
      web3WalletButtonsBlockButton: undefined,
    },
  },
  waitlist: {
    start: {
      actionLink: undefined,
      actionText: undefined,
      formButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    success: {
      message: undefined,
      subtitle: undefined,
      title: undefined,
    },
  },
} as const;
